import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";

import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import isNil from "lodash/isNil";
import { useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import Flx from "../../layout/Flx";
import Txt from "../../typography/Txt";

// ─── Constants ────────────────────────────────────────────────────────────────

const BLUE_CHIP_BG = "#e8f0fe";
const BLUE_CHIP_COLOR = "#1e3a5f";

const filter = createFilterOptions();

const GROUP_ORDER = {
  Applicants: 0,
  "Loan Contacts": 1,
  "Internal Users": 2,
  Custom: 3,
};

// Subtle color coding per group for the role/detail badge
const GROUP_BADGE_STYLES = {
  Applicants: { bg: "#fef3c7", color: "#92400e" },
  "Loan Contacts": { bg: "#e0e7ff", color: "#3730a3" },
  "Internal Users": { bg: "#f1f5f9", color: "#475569" },
  Custom: { bg: "#dcfce7", color: "#166534" },
};

// ─── Component ────────────────────────────────────────────────────────────────

const LoanEmailRecipientSelector = ({
  value,
  onChange,
  sx,
  allowCustomEmail = false,
}) => {
  const loanDrilldown = useSelector((state) => state.loanDrilldown);
  const users = useSelector((state) => state?.users);
  const inputRef = useRef(null);

  // Build grouped options with deduplication by email
  const options = useMemo(() => {
    const seen = new Set();
    const result = [];

    // 1. Borrowers
    (loanDrilldown?.borrowers || [])
      .filter((b) => !isNil(b?.emailAddress))
      .forEach((b) => {
        const email = b.emailAddress.toLowerCase();
        if (seen.has(email)) return;
        seen.add(email);
        result.push({
          label: `${b.firstName} ${b.lastName}`,
          email: b.emailAddress,
          value: b.emailAddress,
          detail: b.entityPercentOwnership
            ? `${b.entityPercentOwnership}% Ownership`
            : "Applicant",
          group: "Applicants",
          phone: b.phone || null,
          sortKey: `${b.firstName} ${b.lastName}`.toLowerCase(),
        });
      });

    // 2. Loan Related Contacts
    (loanDrilldown?.loanRelatedContacts || [])
      .filter((c) => !isNil(c?.emailAddress))
      .forEach((c) => {
        const email = c.emailAddress.toLowerCase();
        if (seen.has(email)) return;
        seen.add(email);
        result.push({
          label: `${c.firstName} ${c.lastName}`,
          email: c.emailAddress,
          value: c.emailAddress,
          detail: c.loanRole || "Contact",
          group: "Loan Contacts",
          company: c.company || null,
          phone: c.phone || null,
          sortKey: `${c.firstName} ${c.lastName}`.toLowerCase(),
        });
      });

    // 3. Internal Users
    (users || []).forEach((u) => {
      const email = u.emailAddress?.toLowerCase();
      if (!email || seen.has(email)) return;
      seen.add(email);
      result.push({
        label: u.fullName || u.emailAddress,
        email: u.emailAddress,
        value: u.emailAddress,
        detail: u.role || "Team Member",
        group: "Internal Users",
        phone: null,
        sortKey: (u.fullName || u.emailAddress).toLowerCase(),
      });
    });

    // Sort: by group order first, then alphabetically within group
    return result.sort((a, b) => {
      const groupDiff =
        (GROUP_ORDER[a.group] ?? 99) - (GROUP_ORDER[b.group] ?? 99);
      if (groupDiff !== 0) return groupDiff;
      return a.sortKey.localeCompare(b.sortKey);
    });
  }, [loanDrilldown?.borrowers, loanDrilldown?.loanRelatedContacts, users]);

  // Resolve selected values back to option objects
  const selectedOptions = useMemo(() => {
    return (value || [])
      .map((v) => {
        const found = options.find((o) => o.value === v);
        if (found) return found;
        // Custom email fallback
        if (allowCustomEmail && typeof v === "string") {
          return {
            label: v,
            email: v,
            value: v,
            detail: "Custom",
            group: "Custom",
            phone: null,
            sortKey: v.toLowerCase(),
            isCustom: true,
          };
        }
        return null;
      })
      .filter(Boolean);
  }, [value, options, allowCustomEmail]);

  return (
    <Autocomplete
      multiple
      freeSolo={allowCustomEmail}
      value={selectedOptions}
      options={options}
      fullWidth
      size="small"
      groupBy={(option) => option.group}
      getOptionLabel={(option) => {
        if (typeof option === "string") return option;
        if (option.label === option.email) return option.email;
        return `${option.label} <${option.email}>`;
      }}
      isOptionEqualToValue={(option, val) => option.value === val.value}
      filterOptions={(opts, params) => {
        const filtered = filter(opts, params);
        const { inputValue } = params;
        const trimmed = inputValue.trim();

        if (
          trimmed &&
          !opts.some((o) => o.email?.toLowerCase() === trimmed.toLowerCase()) &&
          !selectedOptions.some(
            (o) => o.email?.toLowerCase() === trimmed.toLowerCase(),
          )
        ) {
          filtered.push({
            label: trimmed,
            email: trimmed,
            value: trimmed,
            detail: "Custom",
            group: "Custom",
            phone: null,
            sortKey: trimmed.toLowerCase(),
            isCustom: true,
            inputValue: trimmed,
          });
        }
        return filtered;
      }}
      onChange={(_, newValue) => {
        const normalized = newValue.map((item) => {
          if (typeof item === "string") return item.trim();
          if (item?.inputValue) return item.inputValue.trim();
          return item.value;
        });
        onChange(normalized.filter(Boolean));
        if (inputRef.current) inputRef.current.focus();
      }}
      // ── Group header ──────────────────────────────────────────────
      renderGroup={(params) => (
        <li key={params.key}>
          <Box
            sx={{
              position: "sticky",
              top: "-8px",
              zIndex: 1,
              px: 1.5,
              py: 0.75,
              backgroundColor: "#f8fafc",
              borderBottom: "1px solid rgba(0,0,0,0.06)",
              borderTop: "1px solid rgba(0,0,0,0.06)",
            }}
          >
            <Txt
              sx={{
                fontSize: "10px",
                fontWeight: 800,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                color: "#64748b",
              }}
            >
              {params.group}
            </Txt>
          </Box>
          <ul style={{ padding: 0 }}>{params.children}</ul>
        </li>
      )}
      // ── Option row ────────────────────────────────────────────────
      renderOption={(props, option) => {
        const { key, ...optionProps } = props;
        const badgeStyle = GROUP_BADGE_STYLES[option.group] || {};
        return (
          <Box
            key={option.value}
            component="li"
            {...optionProps}
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 1,
              px: "12px !important",
              py: "8px !important",
              "&:hover": { backgroundColor: "#f8fafc" },
            }}
          >
            <Box sx={{ minWidth: 0, flex: 1 }}>
              {option.isCustom ? (
                <Txt
                  sx={{
                    fontSize: "12px",
                    fontWeight: 600,
                    color: "#1e293b",
                  }}
                >
                  Add &quot;{option.email}&quot;
                </Txt>
              ) : (
                <>
                  <Flx ac g={0.75} sx={{ flexWrap: "wrap" }}>
                    <Txt
                      sx={{
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#1e293b",
                        lineHeight: 1.3,
                      }}
                    >
                      {option.label}
                    </Txt>
                    {option.detail && option.detail !== "Team Member" && (
                      <Box
                        sx={{
                          fontSize: "10px",
                          fontWeight: 700,
                          px: 0.75,
                          py: "1px",
                          borderRadius: "4px",
                          backgroundColor: badgeStyle.bg || "#f1f5f9",
                          color: badgeStyle.color || "#475569",
                          lineHeight: 1.4,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {option.detail}
                      </Box>
                    )}
                    {option.detail === "Team Member" ? (
                      <Txt
                        secondary
                        sx={{
                          lineHeight: 1.3,
                        }}
                      >
                        {`<${option.email}${option.phone ? ` · ${formatPhone(option.phone)}` : ""}${option.company ? ` · ${option.company}` : ""}>`}
                      </Txt>
                    ) : null}
                  </Flx>
                  {option.detail !== "Team Member" && (
                    <Txt
                      secondary
                      sx={{
                        lineHeight: 1.3,
                      }}
                    >
                      {option.email}
                      {option.phone ? ` · ${formatPhone(option.phone)}` : ""}
                      {option.company ? ` · ${option.company}` : ""}
                    </Txt>
                  )}
                </>
              )}
            </Box>
          </Box>
        );
      }}
      // ── Chips ─────────────────────────────────────────────────────
      ChipProps={{
        sx: {
          background: BLUE_CHIP_BG,
          color: BLUE_CHIP_COLOR,
          fontWeight: 600,
          height: "22px",
          ".MuiChip-label": {
            fontSize: "11px !important",
          },
          "& .MuiChip-deleteIcon": {
            color: BLUE_CHIP_COLOR,
            opacity: 0.5,
            fontSize: "14px",
            "&:hover": { opacity: 1 },
          },
        },
      }}
      // ── Input styling ─────────────────────────────────────────────
      sx={{
        "& fieldset": { border: "none" },
        ".MuiInputBase-root": { gap: "5px" },
        ".MuiAutocomplete-tag": { margin: 0 },
        ...sx,
      }}
      slotProps={{
        popper: {
          sx: {
            "& .MuiAutocomplete-listbox": {
              py: 0,
              maxHeight: "640px",
            },
          },
        },
        paper: {
          elevation: 3,
          sx: {
            borderRadius: "12px",
            overflow: "hidden",
            border: "1px solid",
            borderColor: "divider",
          },
        },
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          inputRef={inputRef}
          placeholder="Start Typing..."
          fullWidth
        />
      )}
    />
  );
};

// ─── Helpers ──────────────────────────────────────────────────────────────────

const formatPhone = (phone) => {
  if (!phone) return "";
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return phone;
};

export default LoanEmailRecipientSelector;
